import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { useLocationCtx } from "@/context/LocationContext";
import { useData } from "@/context/DataContext";
import Layout from "@/components/Layout";
import Header from "@/components/Header";
import PromoCard from "@/components/PromoCard";
import BusinessCard from "@/components/BusinessCard";
import { Spinner, EmptyState } from "@/components/States";
import { ChevronRight, Star } from "lucide-react";
import { motion } from "framer-motion";

export default function Home() {
  const navigate = useNavigate();
  const { loc } = useLocationCtx();
  const { categoriesResolved, getCategory } = useData();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (loc.state) params.state = loc.state;
    if (loc.city) params.city = loc.city;
    api
      .get("/businesses", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [loc.state, loc.city]);

  const promos = items.filter((b) => b.discount?.badge || b.discount?.title);
  const featured = items.filter((b) => b.featured);

  return (
    <Layout>
      <Header subtitle="Descubre lo mejor de tu ciudad" />

      <section className="px-5 pt-4">
        <h2 className="font-display text-3xl font-black leading-none tracking-tighter text-[#1A1A1A]">
          Descubre promociones y<br />negocios locales cerca de ti
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-[#5C5C5C]" data-testid="home-intro">
          <span className="font-bold text-[#1A1A1A]">SAIPASS Beneficios</span> es el Directorio de Negocios Locales que te ayuda a descubrir promociones, descuentos y excelentes negocios cerca de ti. Creemos que es tiempo de apoyar a nuestros negocios locales y fortalecer la economía de nuestra comunidad.
        </p>
      </section>

      {/* Category quick grid */}
      <section className="px-5 pt-5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1A1A]">Categorías</p>
          <button
            onClick={() => navigate("/categorias")}
            data-testid="see-all-categories"
            className="flex items-center gap-0.5 text-xs font-bold text-[#E54D2E]"
          >
            Ver todas <ChevronRight size={14} />
          </button>
        </div>
        <div className="no-scrollbar mt-3 flex gap-3 overflow-x-auto pb-1">
          {categoriesResolved.slice(0, 12).map((c, i) => (
            <motion.button
              key={c.slug}
              whileTap={{ scale: 0.94 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              data-testid={`home-cat-${c.slug}`}
              onClick={() => navigate(`/categoria/${c.slug}`)}
              className="flex w-16 shrink-0 flex-col items-center gap-1.5"
            >
              <div
                className="flex h-16 w-16 items-center justify-center rounded-2xl border border-black/5"
                style={{ backgroundColor: `${c.color}14` }}
              >
                <c.Icon size={24} style={{ color: c.color }} strokeWidth={2.2} />
              </div>
              <span className="text-center text-[10px] font-bold leading-tight text-[#5C5C5C]">
                {c.label}
              </span>
            </motion.button>
          ))}
        </div>
      </section>

      {loading ? (
        <Spinner />
      ) : (
        <>
          {featured.length > 0 && (
            <section className="pt-6">
              <div className="flex items-center gap-1.5 px-5">
                <Star size={15} className="fill-[#FFB800] text-[#FFB800]" />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1A1A]">
                  Destacados
                </p>
              </div>
              <div className="no-scrollbar mt-3 flex gap-4 overflow-x-auto px-5 pb-2">
                {featured.map((b, i) => (
                  <motion.button
                    key={b.id}
                    whileTap={{ scale: 0.97 }}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    data-testid={`featured-card-${b.id}`}
                    onClick={() => navigate(`/negocio/${b.id}`)}
                    className="relative w-60 shrink-0 overflow-hidden rounded-2xl border-2 border-[#E54D2E]/40 bg-white text-left shadow-lg"
                  >
                    <div className="relative h-32 w-full overflow-hidden bg-[#F3F0E6]">
                      {b.hero_image_url && (
                        <img src={imgUrl(b.hero_image_url)} alt={b.name} className="h-full w-full object-cover" />
                      )}
                      <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-[#1A1A1A] px-2 py-0.5 text-[9px] font-black uppercase text-white">
                        <Star size={9} className="fill-[#FFB800] text-[#FFB800]" /> Destacado
                      </div>
                      {b.discount?.badge && (
                        <div className="absolute right-2 top-2 rounded-full bg-[#E54D2E] px-2 py-0.5 text-[10px] font-black uppercase text-white">
                          {b.discount.badge}
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="truncate font-display text-base font-bold">{b.name}</p>
                      <p className="truncate text-xs text-[#5C5C5C]">{getCategory(b.category).label} · {b.city}</p>
                    </div>
                  </motion.button>
                ))}
              </div>
            </section>
          )}

          {promos.length > 0 && (
            <section className="pt-6">
              <div className="flex items-center justify-between px-5">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1A1A]">
                  Ofertas destacadas 🔥
                </p>
                <button
                  onClick={() => navigate("/ofertas")}
                  data-testid="see-all-offers"
                  className="flex items-center gap-0.5 text-xs font-bold text-[#E54D2E]"
                >
                  Ver todas <ChevronRight size={14} />
                </button>
              </div>
              <div className="no-scrollbar mt-3 flex gap-4 overflow-x-auto px-5 pb-2">
                {promos.map((b, i) => (
                  <PromoCard key={b.id} business={b} index={i} />
                ))}
              </div>
            </section>
          )}

          <section className="px-5 pt-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1A1A]">
              {items.length} negocios
            </p>
            <div className="mt-3 flex flex-col gap-4">
              {items.length === 0 ? (
                <EmptyState title="Aún no hay negocios" subtitle="Prueba con otra ubicación." />
              ) : (
                items.map((b, i) => <BusinessCard key={b.id} business={b} index={i} />)
              )}
            </div>
          </section>
        </>
      )}
    </Layout>
  );
}
